import React, { useState } from 'react';
import { Search, Globe, AlertCircle, CheckCircle, Loader2, Database, TrendingUp, MapPin } from 'lucide-react';

const ManualAffiliateAdd = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const basePath = process.env.REACT_APP_API_URL || '';

  const handleSubmit = async () => {
    setError('');
    setResult(null);

    if (!url.trim()) {
      setError('Please enter a URL.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${basePath}/api/manual-affiliate-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: url.trim() })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to add affiliate.');
      }

      setResult(data);
      setUrl('');
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) handleSubmit();
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg">
                  <Database className="w-6 h-6 text-white" />
                </div>
                Manual Affiliate Add
              </h1>
              <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                Add a URL directly to affiliate_clean via SEMrush data pipeline
              </p>
              <p className="text-sm text-emerald-500 dark:text-emerald-400 mt-2">
                Fetches: Domain Rank, Organic Keywords, Paid Keywords, Top 5 Geos
              </p>
            </div>
            <div className="px-4 py-2 bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 rounded-lg border border-emerald-200 dark:border-emerald-700 shadow-sm">
              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Admin Only</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search Form */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-6">
            <Globe className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Add Affiliate URL</h2>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Enter Domain or Website URL
              </label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., oddschecker.com or https://example.com/path"
                className="w-full px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 hover:shadow-md h-12 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100"
                disabled={loading}
              />
            </div>

            <div className="lg:w-56 w-full">
              <button
                onClick={handleSubmit}
                disabled={loading || !url.trim()}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 hover:shadow-lg shadow-emerald-600/30 disabled:transform-none disabled:shadow-none h-12"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Database className="w-4 h-4" />
                )}
                <span>{loading ? 'Processing...' : 'Add to Database'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6">
          <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-xl p-4 flex items-center gap-2">
            <AlertCircle className="text-red-500 dark:text-red-400 flex-shrink-0" size={20} />
            <span className="text-red-700 dark:text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Success Result */}
      {result && result.success && (
        <div className="mb-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-emerald-500" size={24} />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100">{result.message}</h2>
              </div>
              {result.data?.source && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  result.data.source === 'semrush'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                }`}>
                  {result.data.source === 'semrush' ? 'SEMrush Data' : 'AI Estimated'}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Rank */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Domain Rank</div>
                <div className="text-2xl font-bold text-gray-800 dark:text-slate-100">
                  {result.data.rank?.toLocaleString() || 'N/A'}
                </div>
              </div>

              {/* Organic Traffic */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400 mb-1">
                  <TrendingUp size={12} /> Organic Traffic
                </div>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {result.data.organicTraffic?.toLocaleString() || '0'}
                </div>
              </div>

              {/* Paid Traffic */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Paid Traffic</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {result.data.adwordsTraffic?.toLocaleString() || '0'}
                </div>
              </div>

              {/* Total Traffic */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Total Traffic</div>
                <div className="text-2xl font-bold text-gray-800 dark:text-slate-100">
                  {result.data.totalTraffic?.toLocaleString() || '0'}
                </div>
              </div>
            </div>

            {/* Keywords */}
            {result.data.keywords && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Organic Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {result.data.keywords.split(', ').map((kw, i) => (
                    <span key={i} className="px-3 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full text-xs border border-emerald-200 dark:border-emerald-700">
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {result.data.paidKeywords && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Paid Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {result.data.paidKeywords.split(', ').map((kw, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs border border-blue-200 dark:border-blue-700">
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Top Geos */}
            {result.data.topGeos && result.data.topGeos.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2 flex items-center gap-1">
                  <MapPin size={14} /> Top Geos
                </h3>
                <div className="flex flex-wrap gap-2">
                  {result.data.topGeos.map((geo, i) => (
                    <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-full text-xs border border-gray-200 dark:border-slate-700">
                      {geo}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualAffiliateAdd;
